import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Copy, Check, RefreshCw, Download, Plus, Trash2, Edit2,
  Loader2, X, Save, Globe, ExternalLink, Tv,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { epgService, EpgAccess, EpgUrls, getErrorMessage } from '@/services';

export default function Epg() {
  const { t } = useTranslation();
  const [urls, setUrls] = useState<EpgUrls | null>(null);
  const [accessEntries, setAccessEntries] = useState<EpgAccess[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isGeneratingLinear, setIsGeneratingLinear] = useState(false);
  const [copiedField, setCopiedField] = useState<string | null>(null);

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [editingEntry, setEditingEntry] = useState<EpgAccess | null>(null);
  const [formData, setFormData] = useState({ server_name: '', server_ip: '', description: '' });
  const [isSaving, setIsSaving] = useState(false);

  // Inline delete confirmation
  const [deletingId, setDeletingId] = useState<number | null>(null);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const [urlsData, accessData] = await Promise.all([
        epgService.getUrls(),
        epgService.listAccess(),
      ]);
      setUrls(urlsData);
      setAccessEntries(accessData);
    } catch (error) {
      toast.error(getErrorMessage(error) || t('common.error'));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const copyToClipboard = async (text: string, field: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
    } catch {
      toast.error(t('common.copyFailed'));
    }
  };

  const handleGenerate = async () => {
    setIsGenerating(true);
    try {
      const result = await epgService.generateEpg(7);
      toast.success(t('epg.epgGenerated', { events: result.events, competitions: result.competitions }));
    } catch (error) {
      toast.error(getErrorMessage(error) || t('common.error'));
    } finally {
      setIsGenerating(false);
    }
  };

  const handleGenerateLinear = async () => {
    setIsGeneratingLinear(true);
    try {
      const result = await epgService.generateLinear();
      toast.success(`${t('epg.linearGenerated', { channels: result.channels, programs: result.programs })}`);
    } catch (error) {
      toast.error(getErrorMessage(error) || t('common.error'));
    } finally {
      setIsGeneratingLinear(false);
    }
  };

  const handleDownloadEpg = async () => {
    try {
      const blob = await epgService.downloadEpg(7);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'epg.xml';
      a.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(getErrorMessage(error) || t('common.error'));
    }
  };

  const handleDownloadLinear = async () => {
    try {
      const blob = await epgService.downloadLinear();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'linear.xml';
      a.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(getErrorMessage(error) || t('common.error'));
    }
  };

  const openAddModal = () => {
    setEditingEntry(null);
    setFormData({ server_name: '', server_ip: '', description: '' });
    setShowModal(true);
  };

  const openEditModal = (entry: EpgAccess) => {
    setEditingEntry(entry);
    setFormData({
      server_name: entry.server_name,
      server_ip: entry.server_ip,
      description: entry.description || '',
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!formData.server_name || !formData.server_ip) {
      toast.error(t('validation.required'));
      return;
    }

    setIsSaving(true);
    try {
      if (editingEntry) {
        await epgService.updateAccess(editingEntry.id, formData);
        toast.success(t('common.updated'));
      } else {
        await epgService.createAccess(formData);
        toast.success(t('common.created'));
      }
      setShowModal(false);
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error) || t('common.error'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await epgService.deleteAccess(id);
      toast.success(t('common.deleted'));
      setDeletingId(null);
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error) || t('common.error'));
    }
  };

  const handleToggleActive = async (entry: EpgAccess) => {
    try {
      await epgService.updateAccess(entry.id, { is_active: !entry.is_active });
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error) || t('common.error'));
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <h1 className="text-2xl font-bold text-dark-100">{t('epg.title')}</h1>
        <div className="card animate-pulse space-y-4">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="h-12 bg-dark-700 rounded" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-dark-100">{t('epg.title')}</h1>
        <p className="text-dark-400 mt-1">{t('epg.generateInfo')}</p>
      </div>

      {/* EPG URLs */}
      <div className="card">
        <h2 className="text-lg font-semibold text-dark-100 mb-4">{t('epg.urls')}</h2>
        <div className="space-y-3">
          {/* Standard EPG URL */}
          <div className="flex items-center gap-3">
            <span className="text-dark-400 text-sm w-28 shrink-0">{t('epg.epgUrl')}</span>
            <div className="flex-1 bg-dark-800 rounded-lg px-4 py-2 text-sm font-mono text-dark-300 truncate">
              {urls?.epgUrl || '-'}
            </div>
            <button
              onClick={() => urls && copyToClipboard(urls.epgUrl, 'epg')}
              className="btn-secondary btn-sm"
            >
              {copiedField === 'epg' ? <Check size={16} className="text-success-600" /> : <Copy size={16} />}
              {copiedField === 'epg' ? t('epg.copied') : t('epg.copyUrl')}
            </button>
          </div>

          {/* Linear EPG URL */}
          <div className="flex items-center gap-3">
            <span className="text-dark-400 text-sm w-28 shrink-0">{t('epg.linearUrl')}</span>
            <div className="flex-1 bg-dark-800 rounded-lg px-4 py-2 text-sm font-mono text-dark-300 truncate">
              {urls?.linearUrl || '-'}
            </div>
            <button
              onClick={() => urls && copyToClipboard(urls.linearUrl, 'linear')}
              className="btn-secondary btn-sm"
            >
              {copiedField === 'linear' ? <Check size={16} className="text-success-600" /> : <Copy size={16} />}
              {copiedField === 'linear' ? t('epg.copied') : t('epg.copyUrl')}
            </button>
          </div>
        </div>
      </div>

      {/* Palinsesto Web pubblico */}
      <div className="card">
        <h2 className="text-lg font-semibold text-dark-100 mb-1 flex items-center gap-2">
          <Tv size={18} className="text-primary-600" />
          {t('epg.publicSchedule')}
        </h2>
        <p className="text-dark-400 text-sm mb-4">{t('epg.publicScheduleInfo')}</p>
        <div className="space-y-3">
          <div className="flex flex-wrap items-center gap-3">
            <span className="text-dark-400 text-sm w-28 shrink-0">{t('epg.publicScheduleUrl')}</span>
            <div className="flex-1 min-w-[200px] bg-dark-800 rounded-lg px-4 py-2 text-sm font-mono text-dark-300 truncate">
              {urls?.scheduleUrl || '-'}
            </div>
            <button
              onClick={() => urls && copyToClipboard(urls.scheduleUrl, 'schedule')}
              className="btn-secondary btn-sm"
            >
              {copiedField === 'schedule' ? <Check size={16} className="text-success-600" /> : <Copy size={16} />}
              {copiedField === 'schedule' ? t('epg.copied') : t('epg.copyUrl')}
            </button>
            {urls?.scheduleUrl && (
              <a href={urls.scheduleUrl} target="_blank" rel="noopener noreferrer" className="btn-secondary btn-sm">
                <ExternalLink size={16} />
                {t('epg.openPage')}
              </a>
            )}
          </div>
        </div>
      </div>

      {/* Generate & Download */}
      <div className="flex gap-3 flex-wrap">
        <button onClick={handleGenerate} disabled={isGenerating} className="btn-secondary">
          {isGenerating ? <Loader2 size={18} className="animate-spin" /> : <RefreshCw size={18} />}
          {t('epg.generate')}
        </button>
        <button onClick={handleGenerateLinear} disabled={isGeneratingLinear} className="btn-secondary">
          {isGeneratingLinear ? <Loader2 size={18} className="animate-spin" /> : <RefreshCw size={18} />}
          {t('epg.generateLinear')}
        </button>
        <button onClick={handleDownloadEpg} className="btn-secondary">
          <Download size={18} />
          {t('epg.downloadEpg')}
        </button>
        <button onClick={handleDownloadLinear} className="btn-secondary">
          <Download size={18} />
          {t('epg.downloadLinear')}
        </button>
      </div>

      {/* IP Access Management */}
      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-dark-100">{t('epg.access')}</h2>
          <button onClick={openAddModal} className="btn-primary btn-sm">
            <Plus size={16} />
            {t('epg.addClient')}
          </button>
        </div>

        {accessEntries.length === 0 ? (
          <div className="text-center py-8">
            <Globe size={32} className="text-dark-600 mx-auto mb-3" />
            <p className="text-dark-400">{t('epg.noAccess')}</p>
          </div>
        ) : (
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>{t('epg.serverName')}</th>
                  <th>{t('epg.serverIp')}</th>
                  <th>{t('common.description')}</th>
                  <th>{t('common.status')}</th>
                  <th>{t('common.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {accessEntries.map((entry) => (
                  <tr key={entry.id}>
                    <td className="font-medium text-dark-100">{entry.server_name}</td>
                    <td className="font-mono text-sm">{entry.server_ip}</td>
                    <td className="text-dark-400">{entry.description || '-'}</td>
                    <td>
                      <button
                        onClick={() => handleToggleActive(entry)}
                        className={`px-2.5 py-1 rounded-full text-xs font-medium cursor-pointer ${
                          entry.is_active
                            ? 'bg-success-50 text-success-600'
                            : 'bg-dark-700 text-dark-500'
                        }`}
                      >
                        {entry.is_active ? t('common.active') : t('common.inactive')}
                      </button>
                    </td>
                    <td>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => openEditModal(entry)}
                          className="p-1.5 rounded-lg hover:bg-dark-700 text-dark-400 hover:text-dark-200 transition-colors"
                        >
                          <Edit2 size={14} />
                        </button>

                        {deletingId === entry.id ? (
                          <div className="flex items-center gap-1">
                            <button
                              onClick={() => handleDelete(entry.id)}
                              className="p-1.5 rounded-lg hover:bg-error-50 text-error-600 transition-colors"
                            >
                              <Check size={14} />
                            </button>
                            <button
                              onClick={() => setDeletingId(null)}
                              className="p-1.5 rounded-lg hover:bg-dark-700 text-dark-400 transition-colors"
                            >
                              <X size={14} />
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => setDeletingId(entry.id)}
                            className="p-1.5 rounded-lg hover:bg-error-50 text-dark-400 hover:text-error-600 transition-colors"
                          >
                            <Trash2 size={14} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Add/Edit Modal */}
      {showModal && (
        <div
          className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
          onClick={() => setShowModal(false)}
        >
          <div className="bg-dark-900 rounded-lg border border-dark-700 w-full max-w-lg" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-dark-700">
              <h3 className="text-lg font-semibold text-dark-100">
                {editingEntry ? t('common.edit') : t('epg.addClient')}
              </h3>
              <button
                onClick={() => setShowModal(false)}
                className="p-1.5 rounded-lg hover:bg-dark-700 text-dark-400"
              >
                <X size={18} />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-dark-300 mb-1.5">
                  {t('epg.serverName')} *
                </label>
                <input
                  type="text"
                  value={formData.server_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, server_name: e.target.value }))}
                  className="input w-full"
                  placeholder="e.g. Main Server"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-dark-300 mb-1.5">
                  {t('epg.serverIp')} *
                </label>
                <input
                  type="text"
                  value={formData.server_ip}
                  onChange={(e) => setFormData(prev => ({ ...prev, server_ip: e.target.value }))}
                  className="input w-full"
                  placeholder="e.g. 192.168.1.100"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-dark-300 mb-1.5">
                  {t('common.description')}
                </label>
                <input
                  type="text"
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  className="input w-full"
                  placeholder={t('common.optional')}
                />
              </div>
            </div>

            <div className="flex justify-end gap-3 px-6 py-4 border-t border-dark-700">
              <button onClick={() => setShowModal(false)} className="btn-secondary">
                {t('common.cancel')}
              </button>
              <button onClick={handleSave} disabled={isSaving} className="btn-primary">
                {isSaving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
                {t('common.save')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
